import { useEffect, useState, useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { api } from "../../lib/api";

const RANGES = [
  { id: "7d",  label: "7 ימים" },
  { id: "30d", label: "30 ימים" },
  { id: "90d", label: "90 ימים" },
  { id: "all", label: "הכל" },
];

function ddmm(iso) {
  // iso = "2026-05-17"
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

/**
 * Renders a continuous daily-signups line chart with cumulative totals.
 * The backend returns per-day counts; we compute cumulative on the
 * fly so the chart stays "growing" rather than spiky-flat.
 */
export default function GrowthChart() {
  const [range, setRange] = useState("30d");
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .adminChartGrowth({ range, include_dummy: false })
      .then((res) => {
        if (!cancelled) setRaw(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setRaw([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [range]);

  // Cumulative totals — accumulate as we walk the days.
  const data = useMemo(() => {
    let total = 0;
    let refer = 0;
    return raw.map((d) => {
      total += d.total_signups;
      refer += d.referral_signups;
      return {
        date: d.date,
        label: ddmm(d.date),
        total,
        referral: refer,
        // Keep the raw daily count so the tooltip can show it too.
        daily_total: d.total_signups,
        daily_referral: d.referral_signups,
      };
    });
  }, [raw]);

  const noActivity = data.length > 0 && data[data.length - 1].total === 0;

  return (
    <div
      className="bg-card/40 rounded-md border border-gold/25 p-5"
      data-testid="growth-chart"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-heading text-sm tracking-[0.2em] uppercase text-gold flex items-center gap-2">
          <TrendingUp className="h-4 w-4" strokeWidth={1.5} />
          גידול משתמשים
        </h3>
        <div className="flex gap-1" data-testid="growth-range-toggle">
          {RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => setRange(r.id)}
              data-testid={`growth-range-${r.id}`}
              className={`px-2.5 py-1 text-xs rounded-sm transition ${
                range === r.id
                  ? "bg-gold/15 text-gold border border-gold/30"
                  : "text-muted-foreground border border-transparent hover:text-foreground"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-[260px]" data-testid="growth-chart-canvas">
        {loading ? (
          <div className="h-full grid place-items-center text-xs text-muted-foreground">
            טוען נתונים…
          </div>
        ) : noActivity ? (
          <div className="h-full grid place-items-center text-center">
            <div>
              <p className="text-sm text-muted-foreground">
                עוד אין הצטרפויות לטווח זה.
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                החל את הטווח על "הכל" כדי לראות היסטוריה ארוכה יותר.
              </p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 8, left: 8, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "currentColor" }}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 11, fill: "currentColor" }}
                width={32}
                allowDecimals={false}
              />
              <Tooltip
                cursor={{ stroke: "#B8893B", strokeWidth: 1, opacity: 0.3 }}
                contentStyle={{
                  background: "var(--card)",
                  border: "1px solid rgba(184,137,59,0.4)",
                  borderRadius: 4,
                  fontSize: 12,
                  direction: "rtl",
                }}
                formatter={(value, name) => [value, name]}
                labelFormatter={(label, payload) => {
                  if (!payload || !payload.length) return label;
                  const p = payload[0].payload;
                  return `${label} · יומי: ${p.daily_total} (${p.daily_referral} מהפניה)`;
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: 12, direction: "rtl" }}
                iconType="line"
              />
              <Line
                type="monotone"
                dataKey="total"
                name="סה״כ"
                stroke="#B8893B"
                strokeWidth={2.5}
                dot={{ r: 2.5, fill: "#B8893B" }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="referral"
                name="מהפניה"
                stroke="#1E2A3A"
                strokeWidth={1.8}
                strokeDasharray="4 4"
                dot={{ r: 2, fill: "#1E2A3A" }}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
